import { useTranslation } from "react-i18next";
import type { PdfIntake } from "./use-pdf-intake";
import { Alert, Button, Spinner } from "@/components/ui";

// The three-way "round system" shown after a PDF is picked in a framework's
// start form. A PDF is NOT extracted into the source box; instead the learner
// chooses how to use it:
//   • Study only        → open the marking workspace standalone (no generation)
//   • Mark, then build   → mark the PDF; the marks seed the teacher discussion
//   • Build now          → skip marking; the PDF still grounds generation
// Presentational: the parent owns the savePdf/startRun/navigate side-effects.
export function RoundChooser({
  intake,
  busy,
  onStudy,
  onMark,
  onGenerate,
  onCancel,
}: {
  intake: PdfIntake;
  busy: boolean;
  onStudy: () => void;
  onMark: () => void;
  onGenerate: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  if (intake.processing) {
    return (
      <div className="card flex items-center gap-3 p-5 text-sm text-ink-faint">
        <Spinner className="h-4 w-4" /> {intake.note || t("Reading the PDF…")}
      </div>
    );
  }

  return (
    <div className="card space-y-4 p-5">
      <div>
        <p className="text-sm font-medium text-ink">
          📄 {intake.fileName}
          {intake.chars > 0 ? (
            <span className="font-normal text-ink-faint"> · {t("{{count}} characters", { count: intake.chars })}</span>
          ) : (
            <span className="font-normal text-ink-faint"> · {t("scanned — mark it visually")}</span>
          )}
        </p>
        <p className="mt-1 text-sm text-ink-faint">
          {t("How do you want to use this PDF? You don't have to paste its text — Eddi reads the PDF itself.")}
        </p>
      </div>

      {intake.error && <Alert>{intake.error}</Alert>}

      <div className="grid gap-3 sm:grid-cols-3">
        <ChoiceButton
          title={t("Mark, then build")}
          body={t("Open the PDF, mark what to master and look things up. Your marks steer the question set.")}
          recommended
          disabled={busy}
          onClick={onMark}
        />
        <ChoiceButton
          title={t("Build now")}
          body={t("Skip marking. Eddi reads the whole PDF and builds a set from it straight away.")}
          disabled={busy}
          onClick={onGenerate}
        />
        <ChoiceButton
          title={t("Study only")}
          body={t("Just read and mark — snip figures, ask questions. Turn it into a set whenever you like.")}
          disabled={busy}
          onClick={onStudy}
        />
      </div>

      <div className="flex items-center gap-3">
        {busy && (
          <span className="flex items-center gap-2 text-sm text-ink-faint">
            <Spinner className="h-4 w-4" /> {t("Starting…")}
          </span>
        )}
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          {t("Choose a different file")}
        </Button>
      </div>
    </div>
  );
}

function ChoiceButton({
  title,
  body,
  recommended,
  disabled,
  onClick,
}: {
  title: string;
  body: string;
  recommended?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "flex h-full flex-col rounded-xl border p-3.5 text-left transition disabled:opacity-50 " +
        (recommended
          ? "border-brand-500 bg-brand-50 hover:bg-brand-100"
          : "border-black/10 hover:border-black/20")
      }
    >
      <span className="flex items-center gap-1.5 text-sm font-semibold text-ink">
        {title}
        {recommended && (
          <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {t("Recommended")}
          </span>
        )}
      </span>
      <span className="mt-1 text-xs text-ink-faint">{body}</span>
    </button>
  );
}
